import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../features/auth/authSlice";
import LoginForm from "../components/forms/LoginForm";
import { Mail, Lock } from 'lucide-react';

const logo = process.env.PUBLIC_URL + '/logo.png';

function LoginScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleLogin = (credentials) => {
    dispatch(loginUser(credentials));
  };

  return (
    <div className="login-split-container font-sans">
      {/* Left Panel: Branding & Visuals */}
      <div className="login-left-panel justify-center items-start overflow-hidden relative">
        {/* Background Image/Overlay */}
        <div className="absolute inset-0 primary-gradient-bg opacity-90 z-0"></div>
        
        {/* Decorative elements */}
        <div className="relative z-10 w-full max-w-lg animate-fadeIn">
          <div className="mb-12">
            <div className="w-24 h-24 bg-white p-1 rounded-full shadow-xl flex items-center justify-center mb-8 transform hover:scale-105 transition-transform duration-500">
               <img src={logo} alt="SmartCampusz Logo" className="w-full h-full object-cover rounded-full" />
            </div>
            <h1 className="text-5xl font-black text-white leading-tight tracking-tight">
              Empowering <br />
              <span className="text-teal-200">Education</span> Excellence
            </h1>
            <p className="text-blue-50 mt-6 text-lg font-medium max-w-md opacity-90 leading-relaxed">
              Experience the next generation of school management with SmartCampusz. Unified, efficient, and beautifully designed.
            </p>
          </div>
          
          <div className="flex gap-4 items-center">
            <div className="h-px w-12 bg-teal-200/50"></div>
            <span className="text-teal-100 text-sm font-bold uppercase tracking-[0.2em]">Quality Assurance</span>
          </div>
        </div>

        {/* The Wave Separator */}
        <svg 
          className="wave-separator" 
          viewBox="0 0 100 100" 
          preserveAspectRatio="none"
        >
          <path 
            d="M0 0 C 40 10, 60 90, 100 100 L 100 0 Z" 
            className="fill-white"
          />
        </svg>
      </div>

      {/* Right Panel: Login Form */}
      <div className="login-right-panel animate-fadeIn">
        <div className="w-full max-w-[400px] flex flex-col items-center">
          <div className="lg:hidden mb-10 transform transition-transform hover:scale-110">
            <img src={logo} alt="SmartCampusz Logo" className="w-24 h-24 rounded-full shadow-2xl" />
          </div>

          <div className="w-full text-center lg:text-left mb-10">
            <h2 className="text-4xl font-black text-slate-800 tracking-tight mb-3">Welcome</h2>
            <p className="text-slate-500 font-medium tracking-wide">Log in to your account to continue</p>
          </div>

          <div className="w-full animate-slideInUp">
            <LoginForm 
              onSubmit={handleLogin}
              loading={loading}
              error={error}
            />
          </div>

          <div className="mt-12 w-full text-center">
             <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">
              SmartCampusz &copy; {new Date().getFullYear()}
            </p>
          </div>
        </div>

        {/* Bottom branding (Mobile only) */}
        <div className="lg:hidden absolute bottom-8 text-slate-300 font-bold text-xs uppercase tracking-widest">
          SmartCampusz
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
